// TraceViewPage — Viewer in-app della catena di tracciabilità per una lavorazione.
// Carica via /functions/v1/trace-read?token=<hex>: prodotto finito, allergeni, ingredienti
// con lotto + fornitore + DDT (signed URL). Stesso payload della pagina pubblica trace-page,
// ma renderizzato dentro l'app con stile Ionic e navigazione coerente.

import {
  IonBackButton,
  IonBadge,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonListHeader,
  IonNote,
  IonPage,
  IonSpinner,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import {
  alertCircleOutline,
  cameraOutline,
  checkmarkCircleOutline,
  copyOutline,
  documentTextOutline,
  openOutline,
  qrCodeOutline,
  timeOutline,
} from 'ionicons/icons';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { SUPABASE_URL, TRACE_BASE_URL, getSupabase } from '../../lib/supabase.js';
import { usePageActivity } from '../../lib/hooks/usePageActivity.js';

export interface TraceLotEvidenceV1 {
  photo_urls: string[];
  captured_at: string;
  extracted_batch_lot: string;
  extracted_expiration_date: string | null;
  confidence: number | null;
  match_status: 'matched' | 'not_found';
  match_method: string;
}

export interface TraceLotPhotoCaptureV2 {
  captured_at: string;
  status: 'pending' | 'resolved';
  photo_urls: string[];
  extraction: {
    name: string | null;
    brand: string | null;
    batch_lot: string | null;
    expiration_date: string | null;
    confidence: number;
  } | null;
  ingredient_match: {
    status: 'matched' | 'ambiguous' | 'unassigned';
    score: number | null;
  } | null;
  ingredient: {
    food_name: string;
    qty_used: number;
    uom: string;
    allergens: string[];
  } | null;
  warehouse_match: {
    status: 'matched' | 'not_found_at_cutoff' | 'ambiguous' | 'unreadable';
    batch_lot: string | null;
    expiration_date: string | null;
  } | null;
  origin: TraceLotPhotoOriginV2 | null;
  stock_check: {
    status: 'confirmed' | 'reconciliation_required' | 'not_applicable';
    selected_batch_lot: string | null;
    observed_batch_lot: string | null;
  } | null;
  warnings: string[];
}

export interface TraceLotPhotoOriginV2 {
  batch_lot: string;
  expiration_date: string | null;
  supplier: {
    name: string;
    piva: string | null;
    category: string | null;
  } | null;
  document: {
    kind: string;
    number: string | null;
    date: string | null;
    mime: string | null;
    signed_url: string | null;
  } | null;
}

export interface TraceLotPhotoBatchV2 {
  captured_at: string;
  status: 'pending' | 'resolved';
  resolved_count: number;
  total_count: number;
  captures: TraceLotPhotoCaptureV2[];
}

export function sortTraceLotPhotoCaptures(captures: TraceLotPhotoCaptureV2[]): TraceLotPhotoCaptureV2[] {
  return [...captures].sort((left, right) => Number(left.warehouse_match?.status !== 'matched')
    - Number(right.warehouse_match?.status !== 'matched'));
}

export function splitTraceLotPhotoCaptures(captures: TraceLotPhotoCaptureV2[]): {
  matched: TraceLotPhotoCaptureV2[];
  unassociated: TraceLotPhotoCaptureV2[];
} {
  const sorted = sortTraceLotPhotoCaptures(captures);
  return {
    matched: sorted.filter((capture) => capture.warehouse_match?.status === 'matched'),
    unassociated: sorted.filter((capture) => capture.warehouse_match?.status !== 'matched'),
  };
}

interface TraceInput {
  food_name: string | null;
  batch_lot: string | null;
  origin_allowed?: boolean;
  qty_used: number | null;
  uom: string | null;
  allergens: string[];
  supplier: { name: string; piva: string | null; category: string | null } | null;
  document: {
    kind: string;
    number: string | null;
    date: string | null;
    mime: string | null;
    signed_url: string | null;
  } | null;
  lot_evidence_v1?: TraceLotEvidenceV1 | null;
}

interface TracePayload {
  production: {
    id: string;
    name: string;
    qty: number | null;
    uom: string | null;
    produced_at: string;
    expires_at: string | null;
    shelf_life_minutes: number | null;
    allergens: string[];
    notes: string | null;
  };
  business: { name: string | null; city: string | null } | null;
  inputs: TraceInput[];
  lot_photo_batches_v2?: TraceLotPhotoBatchV2[];
  issued_at: string;
}

async function fetchTrace(token: string): Promise<TracePayload> {
  const { data: sess } = await getSupabase().auth.getSession();
  const auth: Record<string, string> = sess.session?.access_token ? { Authorization: `Bearer ${sess.session.access_token}` } : {};
  const res = await fetch(
    `${SUPABASE_URL.replace(/\/$/, '')}/functions/v1/trace-read?token=${encodeURIComponent(token)}`,
    { headers: auth },
  );
  if (!res.ok) {
    if (res.status === 404) throw new Error('Lavorazione non trovata o token non valido');
    throw new Error(`Errore ${res.status}`);
  }
  return (await res.json()) as TracePayload;
}

function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('it-IT', { dateStyle: 'long', timeStyle: 'short' });
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('it-IT');
}

export function lotEvidencePresentation(status: TraceLotEvidenceV1['match_status']): {
  color: 'success' | 'warning';
  label: string;
  note: string;
  allowOrigin: boolean;
} {
  return status === 'matched'
    ? {
        color: 'success',
        label: 'Match in magazzino',
        note: 'Lotto collegato alla registrazione di magazzino.',
        allowOrigin: true,
      }
    : {
        color: 'warning',
        label: 'Non in magazzino',
        note: 'Evidenza fotografica dichiarata; nessun DDT o fornitore collegato.',
        allowOrigin: false,
      };
}

export function formatLotEvidenceConfidence(confidence: number | null): string | null {
  if (confidence == null || !Number.isFinite(confidence)) return null;
  const percent = confidence <= 1 ? confidence * 100 : confidence;
  return `${Math.round(percent)}%`;
}

export function lotPhotoWarningText(warning: string): string {
  const known: Record<string, string> = {
    ingredient_ambiguous: 'Ingrediente non associato in modo univoco.',
    ingredient_unassigned: 'Foto non associata a un ingrediente della ricetta.',
    lot_not_found_at_cutoff: 'Lotto non presente in magazzino al momento della produzione.',
    lot_ambiguous_at_cutoff: 'Più lotti di magazzino risultano compatibili al momento della produzione.',
    warehouse_origin_ambiguous: 'Il lotto ha più provenienze compatibili; nessun documento è stato scelto arbitrariamente.',
    lot_unreadable: 'Lotto non leggibile nella fotografia.',
    stock_reconciliation_required: 'Il lotto fotografato richiede una verifica dello scarico di magazzino.',
    stock_capture_incomplete: 'Parte del consumo di magazzino non è coperta dalle foto. La produzione non è stata bloccata; verificare la tracciabilità.',
    analysis_failed: 'Analisi AI non completata: verificare manualmente questa fotografia.',
  };
  return known[warning] ?? 'Avviso di analisi da verificare.';
}

export function lotPhotoCapturePresentation(capture: Pick<
  TraceLotPhotoCaptureV2,
  'status' | 'ingredient_match' | 'warehouse_match' | 'stock_check'
>): {
  color: 'success' | 'warning' | 'medium';
  label: string;
  note: string;
} {
  if (capture.status === 'pending') {
    return {
      color: 'medium',
      label: 'Analisi AI in corso',
      note: 'Le foto sono state salvate senza rallentare la produzione.',
    };
  }
  if (capture.ingredient_match?.status === 'ambiguous') {
    return {
      color: 'warning',
      label: 'Ingrediente ambiguo',
      note: 'Nessun ingrediente è stato scelto arbitrariamente.',
    };
  }
  if (capture.ingredient_match?.status === 'unassigned') {
    return {
      color: 'warning',
      label: 'Ingrediente non associato',
      note: 'La foto resta disponibile per una verifica successiva.',
    };
  }
  if (capture.warehouse_match?.status === 'matched') {
    return capture.stock_check?.status === 'reconciliation_required'
      ? {
          color: 'warning',
          label: 'Match trovato, scarico da verificare',
          note: 'La provenienza è stata individuata, ma lo stock richiede riconciliazione.',
        }
      : {
          color: 'success',
          label: 'Match trovato e scarico registrato',
          note: 'La quantità della ricetta è stata scaricata dal lotto fotografato.',
        };
  }
  if (capture.warehouse_match?.status === 'not_found_at_cutoff') {
    return {
      color: 'warning',
      label: 'Lotto non presente in magazzino',
      note: 'Nessun DDT o fornitore è stato collegato automaticamente. La produzione non è stata bloccata.',
    };
  }
  if (capture.warehouse_match?.status === 'ambiguous') {
    return {
      color: 'warning',
      label: 'Match di magazzino ambiguo',
      note: 'Nessuna provenienza è stata scelta arbitrariamente.',
    };
  }
  return {
    color: 'warning',
    label: 'Lotto non leggibile',
    note: 'La foto resta disponibile per una verifica successiva.',
  };
}

export function confirmedTraceLotPhotoOrigin(capture: Pick<
  TraceLotPhotoCaptureV2,
  'status' | 'ingredient_match' | 'warehouse_match' | 'stock_check' | 'origin'
>): TraceLotPhotoOriginV2 | null {
  const origin = capture.origin;
  if (
    capture.status !== 'resolved'
    || capture.ingredient_match?.status !== 'matched'
    || capture.warehouse_match?.status !== 'matched'
    || capture.stock_check?.status !== 'confirmed'
    || !origin
    || origin.batch_lot !== capture.warehouse_match.batch_lot
    || origin.expiration_date !== capture.warehouse_match.expiration_date
  ) return null;
  return origin;
}

export function TraceLotPhotoOriginDetails({
  capture,
}: {
  capture: TraceLotPhotoCaptureV2;
}): JSX.Element | null {
  const origin = confirmedTraceLotPhotoOrigin(capture);
  if (!origin) return null;
  const documentLabel = origin.document?.kind === 'fattura' ? 'Fattura' : 'DDT';
  return (
    <div
      aria-label={`Origine confermata del lotto ${origin.batch_lot}`}
      style={{ marginTop: 6, fontSize: 13, lineHeight: 1.5 }}
    >
      <p style={{ margin: 0 }}>
        Lotto usato <b>{origin.batch_lot}</b>
        {origin.expiration_date ? ` · scadenza ${fmtDate(origin.expiration_date)}` : ''}
      </p>
      {origin.supplier && (
        <p style={{ margin: '3px 0 0' }}>
          Fornitore: <b>{origin.supplier.name}</b>
          {origin.supplier.piva ? ` · P.IVA ${origin.supplier.piva}` : ''}
        </p>
      )}
      {origin.document && (
        <p style={{ margin: '3px 0 0' }}>
          {documentLabel}: <b>{origin.document.number ?? '—'}</b>
          {origin.document.date ? ` del ${fmtDate(origin.document.date)}` : ''}
          {origin.document.signed_url && (
            <a
              href={origin.document.signed_url}
              target="_blank"
              rel="noreferrer"
              aria-label={`Apri ${documentLabel} ${origin.document.number ?? ''}`.trim()}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                minHeight: 44,
                marginLeft: 8,
                fontWeight: 600,
              }}
            >
              Apri documento
            </a>
          )}
        </p>
      )}
    </div>
  );
}

export function TraceViewPage(): JSX.Element {
  const { token } = useParams<{ token: string }>();
  const { isActive } = usePageActivity(`/warehouse/trace/${token}`);
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['trace-read', token],
    queryFn: () => fetchTrace(token),
    enabled: !!token && isActive,
    // T-566: l'allergene anagrafico può essere corretto dopo la produzione;
    // ogni riapertura deve quindi ricostruire il riepilogo dalla filiera live.
    staleTime: 0,
    refetchOnMount: 'always',
    refetchInterval: (query) => {
      const trace = query.state.data as TracePayload | undefined;
      return trace?.lot_photo_batches_v2?.some((batch) => batch.status === 'pending')
        ? 15_000
        : false;
    },
  });

  const publicUrl = `${TRACE_BASE_URL}/t/${encodeURIComponent(token)}`;

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start"><IonBackButton defaultHref="/warehouse/productions" /></IonButtons>
          <IonTitle>Tracciabilità</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={() => window.open(publicUrl, '_blank')} aria-label="Apri pagina pubblica">
              <IonIcon slot="icon-only" icon={openOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        {isLoading && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
            <IonSpinner name="dots" />
          </div>
        )}
        {error && (
          <IonItem color="danger">
            <IonLabel className="ion-text-wrap">
              <h3>Errore</h3>
              <p>{(error as Error).message}</p>
            </IonLabel>
            <IonButton slot="end" fill="outline" onClick={() => void refetch()}>Riprova</IonButton>
          </IonItem>
        )}
        {data && (
          <>
            <IonList inset>
              <IonItem lines="none">
                <IonIcon icon={qrCodeOutline} slot="start" color="primary" />
                <IonLabel>
                  <h2 style={{ fontWeight: 700 }}>{data.production.name}</h2>
                  {(data.production.qty != null) && (
                    <p>{data.production.qty} {data.production.uom ?? ''}</p>
                  )}
                  <p style={{ color: '#666' }}>
                    Prodotto il {fmtDateTime(data.production.produced_at)}
                    {data.business?.name ? ` · ${data.business.name}` : ''}
                    {data.business?.city ? ` (${data.business.city})` : ''}
                  </p>
                  {data.production.expires_at && (
                    <p style={{ color: 'var(--ion-color-warning)', fontWeight: 600 }}>
                      Scade il {fmtDateTime(data.production.expires_at)}
                      {data.production.shelf_life_minutes
                        ? ` · shelf-life ${Math.round(data.production.shelf_life_minutes / 60)} h`
                        : ''}
                    </p>
                  )}
                </IonLabel>
              </IonItem>
              {data.production.allergens.length > 0 && (
                <IonItem>
                  <IonLabel className="ion-text-wrap">
                    <h3>Allergeni aggregati</h3>
                    <div style={{ marginTop: 6 }}>
                      {data.production.allergens.map((a) => (
                        <IonBadge
                          key={a}
                          color="warning"
                          style={{ marginRight: 4, marginBottom: 4 }}
                        >
                          {a}
                        </IonBadge>
                      ))}
                    </div>
                  </IonLabel>
                </IonItem>
              )}
              {data.production.notes && (
                <IonItem>
                  <IonLabel className="ion-text-wrap">
                    <h3>Note</h3>
                    <p>{data.production.notes}</p>
                  </IonLabel>
                </IonItem>
              )}
            </IonList>

            {(data.lot_photo_batches_v2?.length ?? 0) > 0 && (
              <IonList inset aria-label="Analisi fotografie dei lotti">
                <IonListHeader>
                  <IonLabel>Foto lotti ingrediente</IonLabel>
                </IonListHeader>
                {data.lot_photo_batches_v2?.flatMap((batch, batchIndex) => {
                  // I collegamenti confermati conservano la priorità; le
                  // materie prime lette ma non presenti/associabili sono una
                  // sezione conclusiva, non una sequenza di alert.
                  const { matched, unassociated } = splitTraceLotPhotoCaptures(batch.captures);
                  const renderCapture = (capture: TraceLotPhotoCaptureV2, captureIndex: number): JSX.Element => {
                    const presentation = lotPhotoCapturePresentation(capture);
                    const confirmedOrigin = confirmedTraceLotPhotoOrigin(capture);
                    const confidence = capture.extraction
                      ? formatLotEvidenceConfidence(capture.extraction.confidence)
                      : null;
                    const lot = capture.extraction?.batch_lot
                      ?? capture.warehouse_match?.batch_lot
                      ?? null;
                    const unmatched = capture.status === 'resolved'
                      && capture.warehouse_match?.status !== 'matched';
                    const photoName = capture.ingredient?.food_name
                      ?? capture.extraction?.name
                      ?? (unmatched ? 'Materia prima fotografata non associata' : 'Confezione fotografata');
                    const pending = capture.status === 'pending';
                    return (
                      <IonItem
                        key={`${batchIndex}-${captureIndex}-${capture.captured_at}`}
                        lines={captureIndex === batch.captures.length - 1 ? 'none' : 'inset'}
                      >
                        <IonLabel className="ion-text-wrap">
                          <div
                            aria-live="polite"
                            style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}
                          >
                            <IonIcon
                              icon={pending
                                ? timeOutline
                                : presentation.color === 'success'
                                  ? checkmarkCircleOutline
                                  : alertCircleOutline}
                              color={presentation.color}
                              aria-hidden="true"
                            />
                            <IonBadge color={presentation.color}>{presentation.label}</IonBadge>
                            <IonNote style={{ fontSize: 11 }}>
                              {batch.resolved_count}/{batch.total_count} elaborate
                            </IonNote>
                          </div>
                          <h3 style={{ marginTop: 8, fontWeight: 600 }}>{photoName}</h3>
                          {capture.extraction?.brand && (
                            <p style={{ marginTop: 2, fontSize: 12 }}>{capture.extraction.brand}</p>
                          )}
                          {confirmedOrigin
                            ? <TraceLotPhotoOriginDetails capture={capture} />
                            : !pending && (
                            <p style={{ marginTop: 4, fontSize: 13 }}>
                              {lot ? <>Lotto letto <b>{lot}</b></> : 'Lotto non letto'}
                              {capture.extraction?.expiration_date
                                ? ` · scadenza ${fmtDate(capture.extraction.expiration_date)}`
                                : ''}
                              {confidence ? ` · confidenza ${confidence}` : ''}
                            </p>
                            )}
                          <p style={{ marginTop: 3, fontSize: 12, color: '#64748b' }}>
                            {presentation.note} Foto del {fmtDateTime(capture.captured_at)}.
                          </p>
                          {capture.warnings
                            .filter((warning) => warning !== 'lot_not_found_at_cutoff' && warning !== 'lot_ambiguous_at_cutoff')
                            .map((warning) => (
                            <p
                              key={warning}
                              role="alert"
                              style={{ margin: '5px 0 0', color: 'var(--ion-color-warning-shade)', fontSize: 12 }}
                            >
                              <IonIcon
                                icon={alertCircleOutline}
                                aria-hidden="true"
                                style={{ verticalAlign: -2, marginRight: 4 }}
                              />
                              {lotPhotoWarningText(warning)}
                            </p>
                            ))}
                          {capture.photo_urls.length > 0 ? (
                            <div style={{
                              display: 'flex', gap: 8, overflowX: 'auto', marginTop: 10, paddingBottom: 2,
                            }}>
                              {capture.photo_urls.map((url, photoIndex) => (
                                <a
                                  key={`${batchIndex}-${captureIndex}-${photoIndex}`}
                                  href={url}
                                  target="_blank"
                                  rel="noreferrer"
                                  aria-label={`Apri foto lotto ${photoIndex + 1} di ${photoName}`}
                                  style={{ display: 'block', flex: '0 0 auto', minWidth: 96, minHeight: 96 }}
                                >
                                  <img
                                    src={url}
                                    loading="lazy"
                                    alt={`Foto lotto ${photoIndex + 1} di ${photoName}`}
                                    style={{
                                      display: 'block', width: 96, height: 96, objectFit: 'cover',
                                      borderRadius: 10, border: '1px solid #cbd5e1',
                                    }}
                                  />
                                </a>
                              ))}
                            </div>
                          ) : (
                            <p style={{ margin: '6px 0 0', fontSize: 12, color: '#64748b' }}>
                              <IonIcon icon={cameraOutline} style={{ verticalAlign: -2, marginRight: 4 }} />
                              Foto privata non disponibile in questa sessione.
                            </p>
                          )}
                        </IonLabel>
                      </IonItem>
                    );
                  };
                  return [
                    ...matched.map(renderCapture),
                    ...(unassociated.length > 0 ? [
                      <IonItem key={`unassociated-${batchIndex}`} color="light" lines="full">
                        <IonLabel className="ion-text-wrap">
                          <h3>Materie prime fotografate non associate</h3>
                          <p>Foto e lotti letti restano nella tracciabilità, senza creare collegamenti a magazzino o DDT.</p>
                        </IonLabel>
                      </IonItem>,
                    ] : []),
                    ...unassociated.map((capture, index) => renderCapture(capture, matched.length + index)),
                  ];
                })}
              </IonList>
            )}

            <IonList inset>
              <IonListHeader>
                <IonLabel>Catena di tracciabilità ({data.inputs.length} ingredienti)</IonLabel>
              </IonListHeader>
              {data.inputs.length === 0 ? (
                <IonItem><IonLabel color="medium">Nessun ingrediente tracciato.</IonLabel></IonItem>
              ) : (
                data.inputs.map((inp, idx) => {
                  const evidence = inp.lot_evidence_v1 ?? null;
                  const evidenceUi = evidence
                    ? lotEvidencePresentation(evidence.match_status)
                    : null;
                  const confidence = evidence
                    ? formatLotEvidenceConfidence(evidence.confidence)
                    : null;
                  const allowOrigin = inp.origin_allowed ?? evidenceUi?.allowOrigin ?? true;
                  return (
                    <IonItem key={idx} lines={idx === data.inputs.length - 1 ? 'none' : 'inset'}>
                    <IonLabel className="ion-text-wrap">
                      <h3 style={{ fontWeight: 600 }}>{inp.food_name ?? '—'}</h3>
                      <p style={{ fontSize: 13 }}>
                        Lotto <b>{inp.batch_lot ?? '—'}</b>
                        {inp.qty_used != null ? ` · ${inp.qty_used} ${inp.uom ?? ''} usati` : ''}
                      </p>
                      {allowOrigin && (
                        <p style={{ fontSize: 13, color: '#444' }}>
                          Fornitore: <b>{inp.supplier?.name ?? '—'}</b>
                          {inp.supplier?.piva ? ` · P.IVA ${inp.supplier.piva}` : ''}
                        </p>
                      )}
                      {allowOrigin && inp.document ? (
                        <p style={{ fontSize: 13 }}>
                          <IonIcon icon={documentTextOutline} style={{ verticalAlign: -2, marginRight: 4 }} />
                          {inp.document.kind?.toUpperCase() ?? 'DOC'} {inp.document.number ?? '—'}
                          {inp.document.date ? ` del ${fmtDate(inp.document.date)}` : ''}
                          {inp.document.signed_url && (
                            <>
                              {' · '}
                              <a
                                href={inp.document.signed_url}
                                target="_blank"
                                rel="noreferrer"
                                style={{ color: '#0066cc' }}
                              >
                                apri scansione
                              </a>
                            </>
                          )}
                        </p>
                      ) : allowOrigin ? (
                        <p style={{ fontSize: 12, color: '#999' }}>Documento non collegato</p>
                      ) : null}
                      {evidence && evidenceUi && (
                        <div style={{
                          marginTop: 10,
                          padding: 10,
                          borderRadius: 10,
                          border: evidence.match_status === 'matched'
                            ? '1px solid #a7f3d0'
                            : '1px solid #fcd34d',
                          background: evidence.match_status === 'matched' ? '#ecfdf5' : '#fffbeb',
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                            <IonIcon
                              icon={evidence.match_status === 'matched'
                                ? checkmarkCircleOutline
                                : alertCircleOutline}
                              color={evidenceUi.color}
                              aria-hidden="true"
                            />
                            <IonBadge color={evidenceUi.color}>{evidenceUi.label}</IonBadge>
                          </div>
                          <p style={{ margin: '6px 0 0', fontSize: 13 }}>
                            Lotto letto <b>{evidence.extracted_batch_lot}</b>
                            {evidence.extracted_expiration_date
                              ? ` · scadenza ${fmtDate(evidence.extracted_expiration_date)}`
                              : ''}
                            {confidence ? ` · confidenza ${confidence}` : ''}
                          </p>
                          <p style={{ margin: '3px 0 0', fontSize: 12, color: '#64748b' }}>
                            {evidenceUi.note} Foto scattata il {fmtDateTime(evidence.captured_at)}.
                          </p>
                          {evidence.photo_urls.length > 0 ? (
                            <div style={{
                              display: 'flex', gap: 8, overflowX: 'auto', marginTop: 9, paddingBottom: 2,
                            }}>
                              {evidence.photo_urls.map((url, photoIndex) => (
                                <a
                                  key={`${photoIndex}-${url}`}
                                  href={url}
                                  target="_blank"
                                  rel="noreferrer"
                                  aria-label={`Apri foto etichetta lotto ${photoIndex + 1}`}
                                  style={{ display: 'block', flex: '0 0 auto' }}
                                >
                                  <img
                                    src={url}
                                    loading="lazy"
                                    alt={`Foto etichetta lotto ${photoIndex + 1} di ${inp.food_name ?? 'ingrediente'}`}
                                    style={{
                                      display: 'block', width: 88, height: 88, objectFit: 'cover',
                                      borderRadius: 9, border: '1px solid #cbd5e1',
                                    }}
                                  />
                                </a>
                              ))}
                            </div>
                          ) : (
                            <p style={{ margin: '6px 0 0', fontSize: 12, color: '#64748b' }}>
                              <IonIcon icon={cameraOutline} style={{ verticalAlign: -2, marginRight: 4 }} />
                              Foto non ancora disponibile.
                            </p>
                          )}
                        </div>
                      )}
                      {inp.allergens.length > 0 && (
                        <div style={{ marginTop: 6 }}>
                          {inp.allergens.map((a) => (
                            <IonBadge
                              key={a}
                              color="warning"
                              style={{ marginRight: 4, fontSize: 10 }}
                            >
                              {a}
                            </IonBadge>
                          ))}
                        </div>
                      )}
                    </IonLabel>
                    </IonItem>
                  );
                })
              )}
            </IonList>

            <IonList inset>
              <IonItem button onClick={() => window.open(publicUrl, '_blank')}>
                <IonIcon icon={openOutline} slot="start" />
                <IonLabel>Apri pagina pubblica (per cliente)</IonLabel>
              </IonItem>
              <IonItem
                button
                onClick={() => {
                  if (typeof navigator !== 'undefined' && navigator.clipboard) {
                    void navigator.clipboard.writeText(publicUrl);
                  }
                }}
              >
                <IonIcon icon={copyOutline} slot="start" />
                <IonLabel>
                  <h3>Copia URL pubblico</h3>
                  <IonNote style={{ fontSize: 11 }}>{publicUrl}</IonNote>
                </IonLabel>
              </IonItem>
            </IonList>

            <p style={{ fontSize: 11, color: '#999', textAlign: 'center', marginTop: 16 }}>
              Snapshot al {fmtDateTime(data.issued_at)}. I link a scansioni e foto scadono in 15 minuti.
            </p>
          </>
        )}
      </IonContent>
    </IonPage>
  );
}
